import React, { useEffect, useRef, useState } from 'react';
import { Send, Loader2, ShieldCheck, User, Clock, Info, CheckCircle2, ImagePlus, X } from 'lucide-react';
import { api, absoluteUrl } from '../lib/api';
import RichTextEditor from './RichTextEditor';

const htmlIsEmpty = (h) => !h || !h.replace(/<img[^>]*>/gi, 'x').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, '').trim();

/**
 * Two-way support conversation (NOT a live chat). Works via a public per-ticket
 * `token` (no login required). Used on the public /soporte/:token page and
 * inside the member dashboard support tab.
 */
export default function SupportThread({ token, onUpdated }) {
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [justSent, setJustSent] = useState(false);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/support/thread/${token}`);
      setThread(data);
    } catch {
      setError('No encontramos esta conversación. El enlace puede ser incorrecto.');
    }
    setLoading(false);
  }

  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread?.messages?.length]);

  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setError('');
    setUploading(true);
    try {
      for (const f of files.slice(0, 6 - images.length)) {
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await api.post(`/support/thread/${token}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (data?.url) setImages((prev) => [...prev, data.url]);
      }
    } catch {
      setError('No pudimos subir la imagen. Usa JPG/PNG/WebP de máximo 5 MB.');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function send(e) {
    e.preventDefault();
    if (htmlIsEmpty(text) && images.length === 0) return;
    setSending(true);
    setError('');
    try {
      const { data } = await api.post(`/support/thread/${token}/reply`, { html: htmlIsEmpty(text) ? '' : text, images });
      setThread(data);
      setText('');
      setImages([]);
      setJustSent(true);
      setTimeout(() => setJustSent(false), 5000);
      onUpdated && onUpdated();
    } catch {
      setError('No pudimos enviar tu mensaje. Inténtalo de nuevo.');
    }
    setSending(false);
  }

  if (loading) {
    return <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-cmic-500" /></div>;
  }
  if (!thread) {
    return <div className="text-center text-charcoal-500 py-12" data-testid="support-thread-error">{error || 'Conversación no disponible.'}</div>;
  }

  const resolved = thread.status === 'resolved';

  return (
    <div data-testid="support-thread">
      {/* Status + ticket id */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1 border ${resolved ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`} data-testid="support-thread-status">
          <span className={`w-2 h-2 rounded-full ${resolved ? 'bg-emerald-500' : 'bg-amber-500'}`} />
          {resolved ? 'Resuelto' : 'Abierto'}
        </span>
        <span className="text-xs text-charcoal-400 inline-flex items-center gap-1"><Clock size={12} /> Ticket #{String(thread.thread_token || '').slice(0, 8)}</span>
      </div>

      {/* "Not a live chat" notice */}
      <div className="flex items-start gap-2 bg-cmic-50 border border-cmic-100 text-cmic-800 rounded-xl px-4 py-3 mb-4" data-testid="support-not-live-notice">
        <Info size={16} className="mt-0.5 flex-shrink-0 text-cmic-600" />
        <p className="text-sm leading-relaxed">
          <strong>Esto no es un chat en vivo.</strong> Tus mensajes quedan guardados aquí y te responderemos por correo y en esta misma página lo antes posible. No necesitas esperar con la página abierta.
        </p>
      </div>

      {/* Conversation */}
      <div className="rounded-2xl border border-gray-100 bg-slate-50/60 p-3 sm:p-4 max-h-[55vh] overflow-y-auto space-y-3">
        {(thread.messages || []).length === 0 && (
          <p className="text-center text-charcoal-400 text-sm py-6">Escribe tu primer mensaje abajo.</p>
        )}
        {(thread.messages || []).map((m) => {
          const isAdmin = m.from === 'admin';
          return (
            <div key={m.id} className={`flex items-end gap-2 ${isAdmin ? 'justify-start' : 'justify-end flex-row-reverse'}`} data-testid={`support-msg-${m.from}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isAdmin ? 'bg-cmic-600 text-white' : 'bg-charcoal-200 text-charcoal-600'}`}>
                {isAdmin ? <ShieldCheck size={16} /> : <User size={16} />}
              </div>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 shadow-sm ${isAdmin ? 'bg-white border border-gray-200 text-charcoal-800 rounded-bl-sm' : 'bg-cmic-600 text-white rounded-br-sm'}`}>
                <div className={`text-[11px] font-semibold mb-1 ${isAdmin ? 'text-cmic-700' : 'text-white/80'}`}>
                  {isAdmin ? 'Soporte CMIC' : 'Tú'}
                </div>
                {m.subject && <div className={`font-semibold mb-1 ${isAdmin ? 'text-cmic-800' : 'text-white'}`}>{m.subject}</div>}
                {m.html
                  ? <div className={`support-html text-[15px] leading-relaxed ${isAdmin ? '' : 'text-white'}`} dangerouslySetInnerHTML={{ __html: m.html }} />
                  : (m.text && <div className="text-[15px] leading-relaxed whitespace-pre-wrap">{m.text}</div>)}
                {Array.isArray(m.images) && m.images.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2" data-testid={`support-msg-images-${m.from}`}>
                    {m.images.map((u, i) => (
                      <a key={i} href={absoluteUrl(u)} target="_blank" rel="noreferrer" className="block">
                        <img src={absoluteUrl(u)} alt="Adjunto" className="max-w-[160px] max-h-[160px] rounded-lg border border-black/10 object-cover" />
                      </a>
                    ))}
                  </div>
                )}
                {m.created_at && <div className={`text-[10px] mt-1.5 ${isAdmin ? 'text-charcoal-400' : 'text-white/70'}`}>{new Date(m.created_at).toLocaleString('es-MX')}</div>}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {justSent && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 mt-3" data-testid="support-sent-confirm">
          <CheckCircle2 size={15} /> ¡Mensaje enviado! Te responderemos por correo y aquí mismo.
        </div>
      )}
      {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 mt-3">{error}</div>}

      <form onSubmit={send} className="flex flex-col gap-2 mt-4" data-testid="support-thread-form">
        <RichTextEditor
          value={text}
          onChange={setText}
          placeholder="Escribe tu mensaje aquí… Puedes insertar imágenes con el botón de imagen de la barra."
          testid="support-thread-input"
          minHeight={140}
          uploadUrl={`/support/thread/${token}/upload-image`}
        />
        {images.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="support-thread-image-previews">
            {images.map((u, i) => (
              <div key={i} className="relative">
                <img src={absoluteUrl(u)} alt="Adjunto" className="w-16 h-16 rounded-lg object-cover border border-gray-200" />
                <button
                  type="button"
                  onClick={() => setImages((prev) => prev.filter((_, idx) => idx !== i))}
                  data-testid={`support-thread-remove-image-${i}`}
                  className="absolute -top-2 -right-2 bg-rose-600 text-white rounded-full w-5 h-5 flex items-center justify-center shadow"
                  title="Quitar"
                ><X size={12} /></button>
              </div>
            ))}
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" data-testid="support-thread-file-input" />
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || images.length >= 6}
            data-testid="support-thread-attach"
            className="inline-flex items-center gap-2 text-cmic-700 hover:bg-cmic-50 font-medium rounded-xl px-3 py-2 text-sm disabled:opacity-50"
            title="Adjuntar imagen"
          >
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />} Adjuntar imagen
          </button>
          <button
            type="submit"
            disabled={sending || uploading || (htmlIsEmpty(text) && images.length === 0)}
            data-testid="support-thread-send"
            className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white font-semibold rounded-xl px-6 py-2.5 text-sm disabled:opacity-50"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <><Send size={15} /> Enviar mensaje</>}
          </button>
        </div>
      </form>
    </div>
  );
}
